import React, { useCallback, useMemo } from "react";
import { Box, Flex } from "@chakra-ui/react";
import { EstimateSplitCardProps } from "../types";
import { calculateSupplierFulfillingItemsMRP } from "../utils/deliveryItemUtils";
import { useDeliverySelectionValidation } from "../validation/useDeliverySelectionValidation";
import { useManualOptionHandlers } from "../hooks/useManualOptionHandlers";
import { MANUAL_DELIVERY_OPTION_RADIO_VALUE } from "../utils/deliveryOptionSelectionUtils";
import {
  getAddManualDeliveryRestrictionMessage,
  getCustomerAssociateSupplierIds,
  getDeleteDeliveryOptionRestrictionMessage,
  getManualDeliveryEditPermission,
  ManualDeliveryEditPermission,
} from "../utils/manualDeliveryOptionEditPermission";
import {
  buildTradingSelfPickupValidationContext,
  isTradingSelfPickupAllowed,
} from "../utils/estimateTradingSelfPickupValidation";
import { checkCustomerHasMgpAssociation } from "../utils/resolveDefaultEstimateSplitTransactionType";
import { useEstimateSplitSelection } from "./hooks/useEstimateSplitSelection";
import { useEstimateSplitMutations } from "./hooks/useEstimateSplitMutations";
import { useBuyTimeInput } from "./hooks/useBuyTimeInput";
import { SupplierSplitHeader } from "./components/SupplierSplitHeader";
import { SplitActionPills } from "./components/SplitActionPills";
import { PartsListColumn } from "./components/PartsListColumn";
import { DeliveryOptionsPanel } from "./components/DeliveryOptionsPanel";
import { getFullPaymentRouteLabel } from "./utils/paymentRouteLabels";
import { ReadOnlySplitActionDisplay } from "./components/ReadOnlySplitActionDisplay";
import { useGetOrderMediaConfig } from "../../../views/PlaceOrderTest/queryHooks";
import { useGetAllSuppliers } from "../../../views/Orders/queryHooks";
import {
  isSourceSupplierInList,
  parseWhitelistedSuppliersForDeliveryOption,
} from "../utils/parseWhitelistedSuppliersForDeliveryOption";
import { CPTSlabForManualOption } from "../../../views/Orders/enums";
import { DeliverySupplierOption } from "../types";
import { shouldShowAllIamSuppliersInDeliveryOptions } from "../utils/shouldShowAllIamSuppliersInDeliveryOptions";

const IAM_PARENT_SUPPLIER_ID = "62bbfb934de9282bc818d3a1";

const EstimateSplitCard: React.FC<EstimateSplitCardProps> = ({
  estimateNo,
  orderNo,
  supplier,
  selection,
  onSelectionChange,
  isExpired,
  quickEtasMins,
  instantDeliveryMinimumDeliveryCost,
  orderData,
  garaazSupplierIdFromConfig,
  isSingleGaraazInvoiceRequired,
  onOptionAdd,
  onOptionDelete,
  onSplitConfigApplied,
  isReadOnly = false,
  canRemoveParts = false,
  onRemovePart,
}) => {
  const isRemainingItems =
    supplier.supplierId === null || supplier.supplierId === undefined;

  const { data: mobileConfigs, isLoading: isMobileConfigLoading, isError: isMobileConfigError } =
    useGetOrderMediaConfig();
  const { data: allSuppliers } = useGetAllSuppliers();

  const whitelistedSuppliersForRestrictedDeliveryOption = useMemo(
    () =>
      parseWhitelistedSuppliersForDeliveryOption(
        mobileConfigs?.whitelisted_suppliers_for_restricted_delivery_option,
      ),
    [mobileConfigs?.whitelisted_suppliers_for_restricted_delivery_option],
  );

  const isCustomerAssociatedWithGaraaz = useMemo(() => {
    if (!garaazSupplierIdFromConfig) return false;
    return checkCustomerHasMgpAssociation(
      orderData,
      garaazSupplierIdFromConfig,
      allSuppliers,
    );
  }, [orderData, garaazSupplierIdFromConfig, allSuppliers]);

  const sourceSupplierIdForRouting = useMemo(() => {
    return supplier.supplierId ? String(supplier.supplierId) : undefined;
  }, [supplier.supplierId]);

  const isRestrictedSupplier = useMemo(
    () =>
      isSourceSupplierInList(
        sourceSupplierIdForRouting,
        whitelistedSuppliersForRestrictedDeliveryOption.manualFulfillmentRoutingSuppliers,
      ),
    [
      sourceSupplierIdForRouting,
      whitelistedSuppliersForRestrictedDeliveryOption.manualFulfillmentRoutingSuppliers,
    ],
  );

  const isManualFulfillmentRoutingRestricted = useMemo(
    () => {
      if (isCustomerAssociatedWithGaraaz) {
        return false;
      }
      return isRestrictedSupplier;
    },
    [isCustomerAssociatedWithGaraaz, isRestrictedSupplier],
  );

  const shouldShowAllIamSuppliers = useMemo(
    () =>
      shouldShowAllIamSuppliersInDeliveryOptions(
        mobileConfigs?.ff_show_all_iam_suppliers_in_delivery_options,
      ),
    [mobileConfigs?.ff_show_all_iam_suppliers_in_delivery_options],
  );

  const augmentedSupplierOptions = useMemo(() => {
    // When the flag is off, keep whatever was passed in (often undefined → Select hidden).
    if (!shouldShowAllIamSuppliers) {
      return supplier.supplierOptions || [];
    }

    const associateIds = new Set(getCustomerAssociateSupplierIds(orderData));
    const splitSupplierId = String(supplier.supplierId || "").trim();
    const splitSupplierIdLower = splitSupplierId.toLowerCase();

    const getStrId = (val: any) => {
      if (!val) return "";
      if (typeof val === "object") {
        if (val.$oid) return String(val.$oid).trim();
        if (val._id) return String(val._id).trim();
        return "";
      }
      return String(val).trim();
    };

    if (!allSuppliers?.length) {
      return supplier.supplierOptions || [];
    }

    const iamOptions: DeliverySupplierOption[] = allSuppliers
      .filter((s: any) => {
        const parentId = getStrId(s?.parentSupplierId);
        const id = getStrId(s?._id);
        return (
          !!id &&
          (parentId === IAM_PARENT_SUPPLIER_ID ||
            id === IAM_PARENT_SUPPLIER_ID ||
            id.toLowerCase() === splitSupplierIdLower)
        );
      })
      .map((s: any) => {
        const supplierId = getStrId(s._id);
        const firstBranch = s.branches?.[0];
        return {
          supplierId,
          businessName: s.businessName || s.name || "Unknown Supplier",
          branchName: firstBranch?.branchName ?? s.branchName ?? null,
          branchCode: firstBranch?.branchCode ?? s.branchCode ?? null,
          isAssociated: associateIds.has(supplierId.toLowerCase()),
          address: s.address || s.supplierAddress,
        };
      });

    // Always include the split supplier when the full IAM list is shown.
    if (
      splitSupplierId &&
      !iamOptions.some(
        (opt) => opt.supplierId.toLowerCase() === splitSupplierIdLower,
      )
    ) {
      iamOptions.unshift({
        supplierId: splitSupplierId,
        businessName: supplier.supplierName || "Selected Supplier",
        branchName: null,
        branchCode: null,
        isAssociated: associateIds.has(splitSupplierIdLower),
      });
    }

    if (iamOptions.length === 0) {
      return supplier.supplierOptions || [];
    }

    const splitOption =
      iamOptions.find(
        (opt) => opt.supplierId.toLowerCase() === splitSupplierIdLower,
      ) ?? null;
    const associated = iamOptions.filter(
      (opt) =>
        opt.isAssociated &&
        opt.supplierId.toLowerCase() !== splitSupplierIdLower,
    );
    const rest = iamOptions.filter(
      (opt) =>
        opt.supplierId.toLowerCase() !== splitSupplierIdLower &&
        !opt.isAssociated,
    );

    return [...(splitOption ? [splitOption] : []), ...associated, ...rest];
  }, [
    shouldShowAllIamSuppliers,
    supplier.supplierOptions,
    supplier.supplierId,
    supplier.supplierName,
    allSuppliers,
    orderData,
  ]);

  const supplierWithAugmentedOptions = useMemo(
    () => ({ ...supplier, supplierOptions: augmentedSupplierOptions }),
    [supplier, augmentedSupplierOptions]
  );

  const hasAutomatedStandardDelivery = useMemo(
    () =>
      (supplier.deliveryOptions ?? []).some(
        (option) =>
          Number(option.cptSlab) === CPTSlabForManualOption.STANDARD_DELIVERY &&
          option.isSystemGenerated !== false,
      ),
    [supplier.deliveryOptions],
  );

  const manualDeliveryEditPermission = useMemo<ManualDeliveryEditPermission>(() => {
    // Fail closed until permission config is available (loading or fetch error).
    if (isMobileConfigLoading || isMobileConfigError || mobileConfigs == null) {
      return { canEdit: false, reason: null };
    }

    // No automated Standard Delivery → enable manual add/delete.
    if (!hasAutomatedStandardDelivery) {
      return { canEdit: true, reason: null };
    }

    // Automated SD present → disable unless order is on orca_allow_md_order_nos.
    const allowListPermission = getManualDeliveryEditPermission({
      orderData,
      orderNo,
      userId: null,
      whitelistRaw: {
        code: [],
        associateSupplierId: [],
        splitSupplierId: [],
        orderNo: [
          String(orderNo || orderData?.orderNo || orderData?.orderNumber || "x"),
        ],
      },
      allowedOrderNosRaw: mobileConfigs.orca_allow_md_order_nos,
      allowedUserIdsRaw: undefined,
      splitSupplierId: supplier.supplierId,
    });

    return allowListPermission.canEdit
      ? { canEdit: true, reason: null }
      : { canEdit: false, reason: null };
  }, [
    orderData,
    orderNo,
    mobileConfigs,
    isMobileConfigLoading,
    isMobileConfigError,
    supplier.supplierId,
    hasAutomatedStandardDelivery,
  ]);

  const canEditManualDelivery = manualDeliveryEditPermission.canEdit;
  const addManualDeliveryDisabledTooltip = getAddManualDeliveryRestrictionMessage(
    manualDeliveryEditPermission.reason,
  );
  const deleteManualDeliveryDisabledTooltip =
    getDeleteDeliveryOptionRestrictionMessage(
      manualDeliveryEditPermission.reason,
    );

  const supplierFulfillingItemsMRP = useMemo(
    () => calculateSupplierFulfillingItemsMRP(supplier),
    [supplier],
  );

  const tradingSelfPickupContext = useMemo(
    () =>
      buildTradingSelfPickupValidationContext({
        supplierId: supplier.supplierId,
        orderData,
        mgpDistributorConfig: mobileConfigs?.mgp_distributor,
        allSuppliers,
      }),
    [supplier.supplierId, orderData, mobileConfigs?.mgp_distributor, allSuppliers],
  );

  const allowTradingSelfPickup = useMemo(
    () =>
      isTradingSelfPickupAllowed(supplier.supplierId, tradingSelfPickupContext),
    [supplier.supplierId, tradingSelfPickupContext],
  );

  const { errorsByField } = useDeliverySelectionValidation(selection, {
    instantDeliveryMinimumDeliveryCost,
    transactionType: supplierWithAugmentedOptions.transactionType,
    allowTradingSelfPickup,
    isManualFulfillmentRoutingRestricted,
    supplierOptions: supplierWithAugmentedOptions.supplierOptions,
  });

  const splitSelection = useEstimateSplitSelection({
    supplier: supplierWithAugmentedOptions,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
  });

  const buyTime = useBuyTimeInput({
    initialBuyTimeMinutes: supplier.buyTime,
    supplierId: supplier.supplierId,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
    isExpired,
  });

  const mutations = useEstimateSplitMutations({
    estimateNo,
    supplier: supplierWithAugmentedOptions,
    selection,
    onSelectionChange: onSelectionChange || (() => {}),
    onOptionDelete: onOptionDelete || (async () => {}),
    onOptionAdd: onOptionAdd || (() => {}),
    instantDeliveryMinimumDeliveryCost,
    isManualSelected: splitSelection.isManualSelected,
    isExpired,
    tradingSelfPickupContext,
    isManualFulfillmentRoutingRestricted,
  });

  const {
    quickEtaOptions,
    handleManualCPTChange,
    handleManualChargeChange,
    handleQuickEtaSelect,
    handleManualCPTSlabChange,
    handleManualSupplierChange,
  } = useManualOptionHandlers({
    isExpired,
    quickEtasMins,
    currentManualEntry: selection?.deliveryDetails?.manualEntry,
    onManualEntryChange: splitSelection.handleManualOptionUpdate,
    supplierOptions: supplierWithAugmentedOptions.supplierOptions,
    isManualFulfillmentRoutingRestricted,
  });

  const displayTitle = isRemainingItems
    ? "Supplier Not Found"
    : supplier.supplierName;

  const paymentRouteLabel = useMemo(
    () =>
      getFullPaymentRouteLabel(supplier.payableBy, supplier.payableTo),
    [supplier.payableBy, supplier.payableTo],
  );

  const handleRadioChange = useCallback(
    (value: string) => {
      if (value === MANUAL_DELIVERY_OPTION_RADIO_VALUE) {
        splitSelection.handleManualOptionSelect();
      } else {
        splitSelection.handleDeliveryOptionSelect(value);
      }
    },
    [splitSelection],
  );

  const handleManualEntryCancel = useCallback(() => {
    splitSelection.handleManualEntryCancel(isExpired);
  }, [splitSelection, isExpired]);

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px solid"
      borderColor="orange.300"
      overflow="hidden"
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ boxShadow: "md" }}
    >
      <SupplierSplitHeader
        displayTitle={displayTitle}
        supplier={supplier}
        isRemainingItems={isRemainingItems}
        supplierFulfillingItemsMRP={supplierFulfillingItemsMRP}
        actions={
          isReadOnly ? (
            <ReadOnlySplitActionDisplay
              status={supplier.status}
              buyTimeMinutes={supplier.buyTime}
            />
          ) : (
            <SplitActionPills
              selection={selection}
              isExpired={isExpired}
              buyTimeValue={buyTime.buyTimeValue}
              isBuyTimeSelected={splitSelection.isBuyTimeSelected}
              onHold={splitSelection.handleHold}
              onCancel={splitSelection.handleCancel}
              onBuyTimeToggle={buyTime.handleBuyTimeToggle}
              onBuyTimeValueChange={buyTime.handleBuyTimeValueChange}
            />
          )
        }
      />

      <Flex direction={{ base: "column", md: "row" }}>
        <PartsListColumn
          parts={supplier.parts}
          estimateNo={estimateNo}
          splitId={supplier.splitId}
          supplierId={supplier.supplierId}
          orderData={orderData}
          orderNo={orderNo}
          garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
          isSingleGaraazInvoiceRequired={isSingleGaraazInvoiceRequired}
          isRemainingItems={isRemainingItems}
          transactionType={supplier.transactionType}
          discountPercentage={supplier.discountPercentage}
          creditNotePercentage={supplier.creditNotePercentage}
          fulfillmentStatus={supplier.fulfillmentStatus}
          paymentRouteFromLabel={paymentRouteLabel.from}
          paymentRouteToLabel={paymentRouteLabel.to}
          payableBy={supplier.payableBy}
          payableTo={supplier.payableTo}
          isExpired={isExpired}
          onSplitConfigApplied={onSplitConfigApplied || (() => {})}
          isReadOnly={isReadOnly}
          canRemoveParts={
            canRemoveParts && !isRemainingItems && !isReadOnly
          }
          onRemovePart={onRemovePart}
        />
        <DeliveryOptionsPanel
          supplier={supplierWithAugmentedOptions}
          selection={selection}
          estimateNo={estimateNo}
          isExpired={isExpired}
          isRemainingItems={isRemainingItems}
          radioValue={splitSelection.radioValue}
          isManualSelected={splitSelection.isManualSelected}
          isDeliveryOptionSelected={splitSelection.isDeliveryOptionSelected}
          errorsByField={errorsByField}
          quickEtaOptions={quickEtaOptions}
          manualSaveError={mutations.manualSaveError}
          isSavingManualOption={mutations.isSavingManualOption}
          isDeleteDisabled={mutations.isDeleteDisabled}
          isDeletingOption={mutations.isDeletingOption}
          onRadioChange={handleRadioChange}
          onDeliveryOptionSelect={splitSelection.handleDeliveryOptionSelect}
          onManualOptionSelect={splitSelection.handleManualOptionSelect}
          onOptionDelete={mutations.handleOptionDelete}
          onManualCPTSlabChange={handleManualCPTSlabChange}
          onManualCPTChange={handleManualCPTChange}
          onManualChargeChange={handleManualChargeChange}
          onQuickEtaSelect={handleQuickEtaSelect}
          onManualEntryCancel={handleManualEntryCancel}
          onManualEntrySave={mutations.handleManualEntrySave}
          onManualSupplierChange={handleManualSupplierChange}
          onOptionSupplierChange={splitSelection.handleOptionSupplierChange}
          isReadOnly={isReadOnly}
          canEditManualDelivery={canEditManualDelivery}
          addManualDeliveryDisabledTooltip={addManualDeliveryDisabledTooltip}
          deleteManualDeliveryDisabledTooltip={
            deleteManualDeliveryDisabledTooltip
          }
          allowTradingSelfPickup={allowTradingSelfPickup}
          isManualFulfillmentRoutingRestricted={
            isManualFulfillmentRoutingRestricted
          }
        />
      </Flex>
    </Box>
  );
};

export default EstimateSplitCard;
